import React, { useState, useEffect } from 'react';
import { TextField, makeStyles, Button, Checkbox, FormControlLabel } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { toastr } from 'react-redux-toastr';

import Modal from '../common/Modal';
import LoadingScreen from '../../collums-components/components/common/loadingScreen';
import TinyMce from '../../collums-components/components/common/TinyMce/TinyMce';
import SnippetsApi from '../../collums-components/api/SnippetsApi';
import PractitionerApi from '../../collums-components/api/PractitionerApi';
import { modalsButtonStyles } from '../../collums-constants/styles/stylesheets/buttonsStyles';
import { snippetsStyles } from './styles';

const ALL_PRACTITIONERS_OPTION = { id: '__all__', displayName: 'All practitioners' };

const SnippetModal = ({ snippet, isOpen, onClose }) => {
    const [name, setName] = useState(snippet?.name || '');
    const [note, setNote] = useState(snippet?.note || '');
    const [selectedPractitioners, setSelectedPractitioners] = useState([]);
    const [allPractitioners, setAllPractitioners] = useState(false);
    const [addNewPractitioners, setAddNewPractitioners] = useState(snippet?.addNewPractitioners || false);
    const [practitionerOptions, setPractitionerOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const globalStyles = makeStyles(modalsButtonStyles)();
    const classes = makeStyles(snippetsStyles)();

    useEffect(() => {
        PractitionerApi.listActivePractitioners().then(practitioners => {
            setPractitionerOptions(practitioners);
            if (snippet?.practitioners) {
                const snippetPractIds = snippet.practitioners.map(p => p.id || p._id);
                const matched = practitioners.filter(p => snippetPractIds.includes(p.id));
                if (matched.length === practitioners.length && practitioners.length > 0) {
                    setAllPractitioners(true);
                }
                setSelectedPractitioners(matched);
            }
        });
    }, [snippet]);

    const handleSave = async () => {
        if (!name.trim()) {
            return toastr.error('Name is required');
        }
        if (!note.trim()) {
            return toastr.error('Content is required');
        }

        const practitioners = allPractitioners
            ? practitionerOptions.map(p => p.id)
            : selectedPractitioners.map(p => p.id);

        try {
            setIsLoading(true);
            if (snippet) {
                await SnippetsApi.update(snippet.id, { name, note, practitioners, addNewPractitioners });
            } else {
                await SnippetsApi.create({ name, note, practitioners, addNewPractitioners });
            }
            toastr.success(`Snippet ${snippet ? 'updated' : 'created'} successfully`);
            onClose();
        } catch (err) {
            toastr.error(err?.data?.message || 'Something went wrong');
        } finally {
            setIsLoading(false);
        }
    };

    const tinyConfig = {
        selector: '.tinymce',
        plugins: ['lists'],
        toolbar: 'bold italic strikethrough forecolor backcolor | bullist numlist',
        menubar: false,
        statusbar: false,
        inline: false,
        placeholder: 'Write here...',
        branding: false,
        browser_spellcheck: true,
        min_height: 300
    };

    return (
        <>
            {isLoading && <LoadingScreen />}
            <Modal
                id="snippet-modal"
                isOpen={isOpen}
                title={snippet ? 'Edit snippet' : 'Create snippet'}
                size="md"
                onCancel={onClose}
                onClose={onClose}
                dialogClass={classes.snippetModal}
                actions={[
                    <Button key="cancel" className={globalStyles.cancelButton} onClick={onClose}>
                        Cancel
                    </Button>,
                    <Button key="save" className={globalStyles.confirmButton} onClick={handleSave}>
                        Save
                    </Button>
                ]}
            >
                <div className={classes.snippetCard}>
                    <TextField
                        required
                        label="Name"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        variant="outlined"
                        fullWidth
                        style={{ marginBottom: 16 }}
                    />
                    <TinyMce value={note} onChange={val => setNote(val)} init={tinyConfig} />
                    <Autocomplete
                        multiple
                        options={[ALL_PRACTITIONERS_OPTION, ...practitionerOptions]}
                        getOptionLabel={option => option.displayName || `${option.firstName} ${option.lastName}`}
                        value={allPractitioners ? practitionerOptions : selectedPractitioners}
                        onChange={(_, value) => {
                            const hasAllOption = value.some(v => v.id === '__all__');
                            if (hasAllOption) {
                                setAllPractitioners(true);
                                setAddNewPractitioners(true);
                                setSelectedPractitioners(practitionerOptions);
                            } else {
                                if (allPractitioners) {
                                    setAllPractitioners(false);
                                    setAddNewPractitioners(false);
                                }
                                setSelectedPractitioners(value);
                            }
                        }}
                        getOptionDisabled={option => allPractitioners && option.id !== '__all__'}
                        renderOption={option => (
                            <span>{option.displayName || `${option.firstName} ${option.lastName}`}</span>
                        )}
                        renderInput={params => (
                            <TextField {...params} variant="outlined" label="Choose practitioner access" fullWidth />
                        )}
                        style={{ marginTop: 16 }}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={addNewPractitioners}
                                onChange={e => setAddNewPractitioners(e.target.checked)}
                            />
                        }
                        label="Automatically assign to new practitioners"
                        style={{ marginTop: 8 }}
                    />
                </div>
            </Modal>
        </>
    );
};

export default SnippetModal;
